import { useTranslation } from 'react-i18next';
import {
  Thermometer,
  Trash2,
  AlertTriangle,
  Wifi,
  Router,
  Snowflake,
  Flame,
  ChevronDown,
  Zap,
  Droplets,
  Heart,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

// NOTE: TV/Entertainment, Kitchen instructions and Bathroom/Laundry instructions
// were moved OUT of this component into their own topic sections
// (GuestEntertainmentSection, KitchenSection, BathroomSection). This component now
// covers only Climate (AC/heating), Internet, Trash, Emergency and EV.
interface GuestEquipmentSectionProps {
  unit: {
    // AC
    ac_available?: boolean | null;
    ac_instructions?: string | null;
    ac_rules?: string | null;
    heating_type?: string | null;
    heating_instructions?: string | null;
    // Internet
    router_location?: string | null;
    router_reset_instructions?: string | null;
    // Trash
    trash_instructions?: string | null;
    trash_collection_days?: string | null;
    recycling_instructions?: string | null;
    // Emergency
    fuse_box_location?: string | null;
    water_shutoff_location?: string | null;
    first_aid_location?: string | null;
    // EV charger
    ev_charger_available?: boolean | null;
    ev_charger_type?: string | null;
    ev_charger_power_kw?: number | null;
    ev_charger_instructions?: string | null;
  };
  darkMode?: boolean;
  className?: string;
}

function EquipmentSection({
  title,
  children,
  darkMode,
  defaultOpen = false,
}: {
  title: string;
  /** Kept for call-site compatibility; headers no longer render an icon badge. */
  icon?: React.ElementType;
  iconColor?: string;
  children: React.ReactNode;
  darkMode?: boolean;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={cn(
        'rounded-3xl overflow-hidden border transition-all duration-200',
        darkMode
          ? 'bg-slate-800 border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-white border-gray-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_12px_32px_-14px_rgba(15,23,42,0.22)]'
      )}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between p-4 transition-colors',
          darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-gray-50/80'
        )}
      >
        <span className={cn('font-bold text-lg tracking-tight', darkMode ? 'text-white' : 'text-gray-900')}>
          {title}
        </span>
        <ChevronDown
          className={cn(
            'w-5 h-5 transition-transform duration-200',
            darkMode ? 'text-slate-400' : 'text-gray-400',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className={cn('p-4 pt-3 space-y-3', darkMode ? 'border-t border-slate-700/50' : 'border-t border-gray-100')}>
          {children}
        </div>
      </div>
    </div>
  );
}

function InfoItem({
  icon: Icon,
  title,
  children,
  variant = 'default',
  darkMode,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
  variant?: 'default' | 'warning' | 'info';
  darkMode?: boolean;
}) {
  const variantStyles = {
    default: darkMode
      ? 'bg-slate-700/50 border-slate-600/50'
      : 'bg-gray-50 border-gray-100',
    warning: darkMode
      ? 'bg-orange-900/30 border-orange-700/50'
      : 'bg-orange-50 border-orange-200',
    info: darkMode
      ? 'bg-blue-900/30 border-blue-700/50'
      : 'bg-blue-50 border-blue-200',
  };

  const iconColors = {
    // Equipment group accent (amber) so item icons aren't dull grey and match
    // the rest of the portal's coloured iconography.
    default: darkMode ? 'text-amber-400' : 'text-amber-500',
    warning: 'text-orange-500',
    info: 'text-blue-500',
  };

  return (
    <div className={cn('p-4 rounded-xl border', variantStyles[variant])}>
      <div className="flex items-start gap-3">
        <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', iconColors[variant])} />
        <div className="flex-1 min-w-0">
          <h4 className={cn('font-medium text-sm mb-1', darkMode ? 'text-white' : 'text-gray-900')}>
            {title}
          </h4>
          <div className={cn('text-sm', darkMode ? 'text-slate-300' : 'text-gray-600')}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export function GuestEquipmentSection({ unit, darkMode = false, className }: GuestEquipmentSectionProps) {
  const { t } = useTranslation();

  // Check what sections have RENDERABLE content. A section must only appear when
  // it has something to show inside — otherwise the collapsible opens to nothing.
  // AC section renders AC instructions and/or a heating type (+ optional instr).
  const hasAC = !!(
    unit.ac_available ||
    (unit.heating_type && unit.heating_type !== 'none')
  );
  const hasInternet = !!(unit.router_location || unit.router_reset_instructions);
  const hasTrash = !!(unit.trash_instructions || unit.trash_collection_days || unit.recycling_instructions);
  const hasEmergency = !!(unit.fuse_box_location || unit.water_shutoff_location || unit.first_aid_location);
  const hasEV = !!(
    (unit.ev_charger_available && (unit.ev_charger_type || unit.ev_charger_power_kw || unit.ev_charger_instructions)) ||
    unit.ev_charger_instructions
  );

  const heatingLabels: Record<string, string> = {
    ac: t('units.equipment.ac.heatingTypes.ac'),
    central: t('units.equipment.ac.heatingTypes.central'),
    electric: t('units.equipment.ac.heatingTypes.electric'),
    wood: t('units.equipment.ac.heatingTypes.wood'),
    none: t('units.equipment.ac.heatingTypes.none'),
  };

  // Don't render if nothing to show
  if (!unit.ac_rules && !hasAC && !hasInternet && !hasTrash && !hasEmergency && !hasEV) {
    return null;
  }

  const evChargerLabels: Record<string, string> = {
    type2: 'Type 2', type1: 'Type 1', ccs: 'CCS', chademo: 'CHAdeMO', tesla: 'Tesla',
    socket: t('units.equipment.ev.socket', 'Obična utičnica'),
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* AC Rules Warning - Show prominently at top if exists */}
      {unit.ac_rules && (
        <div
          className={cn(
            'p-4 rounded-2xl border-2',
            darkMode
              ? 'bg-orange-900/20 border-orange-600/50'
              : 'bg-orange-50 border-orange-300'
          )}
        >
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-orange-500 shadow-lg shadow-orange-500/30">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className={cn('font-bold mb-1', darkMode ? 'text-orange-300' : 'text-orange-800')}>
                {t('units.equipment.ac.rules')}
              </h4>
              <p className={cn('text-sm whitespace-pre-wrap', darkMode ? 'text-orange-200' : 'text-orange-700')}>
                {unit.ac_rules}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AC & Heating (without rules - shown separately above) */}
      {hasAC && (
        <EquipmentSection
          title={t('units.equipment.ac.title')}
          icon={Thermometer}
          iconColor="bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-500/30"
          darkMode={darkMode}
        >
          {/* "Has AC" is worth telling the guest even without instructions. */}
          {unit.ac_available && (
            <InfoItem icon={Snowflake} title={unit.ac_instructions ? t('units.equipment.ac.instructions') : t('guest.equipment.acTitle', 'Klima uređaj')} darkMode={darkMode}>
              <p className="whitespace-pre-wrap">{unit.ac_instructions || t('guest.equipment.available', 'Na raspolaganju')}</p>
            </InfoItem>
          )}

          {unit.heating_type && unit.heating_type !== 'none' && (
            <InfoItem icon={Flame} title={t('units.equipment.ac.heatingType')} darkMode={darkMode}>
              <p className="font-medium mb-1">{heatingLabels[unit.heating_type] || unit.heating_type}</p>
              {unit.heating_instructions && (
                <p className="whitespace-pre-wrap">{unit.heating_instructions}</p>
              )}
            </InfoItem>
          )}
        </EquipmentSection>
      )}

      {/* Internet / Router */}
      {hasInternet && (
        <EquipmentSection
          title={t('guest.wifi.wifiInfo') || 'Internet'}
          icon={Router}
          iconColor="bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-500/30"
          darkMode={darkMode}
        >
          {unit.router_location && (
            <InfoItem icon={Router} title={t('units.equipment.internet.routerLocation')} darkMode={darkMode}>
              <p>{unit.router_location}</p>
            </InfoItem>
          )}

          {unit.router_reset_instructions && (
            <InfoItem icon={Wifi} title={t('units.equipment.internet.resetInstructions')} darkMode={darkMode}>
              <p className="whitespace-pre-wrap">{unit.router_reset_instructions}</p>
            </InfoItem>
          )}
        </EquipmentSection>
      )}

      {/* Trash & Recycling */}
      {hasTrash && (
        <EquipmentSection
          title={t('units.equipment.trash.title')}
          icon={Trash2}
          iconColor="bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-500/30"
          darkMode={darkMode}
        >
          {unit.trash_instructions && (
            <InfoItem icon={Trash2} title={t('units.equipment.trash.instructions')} darkMode={darkMode}>
              <p className="whitespace-pre-wrap">{unit.trash_instructions}</p>
            </InfoItem>
          )}

          {unit.trash_collection_days && (
            <InfoItem icon={Trash2} title={t('units.equipment.trash.collectionDays')} darkMode={darkMode}>
              <p>{unit.trash_collection_days}</p>
            </InfoItem>
          )}

          {unit.recycling_instructions && (
            <InfoItem icon={Trash2} title={t('units.equipment.trash.recyclingInstructions')} darkMode={darkMode}>
              <p className="whitespace-pre-wrap">{unit.recycling_instructions}</p>
            </InfoItem>
          )}
        </EquipmentSection>
      )}

      {/* Emergency & Safety */}
      {hasEmergency && (
        <EquipmentSection
          title={t('units.equipment.emergency.title')}
          icon={Heart}
          iconColor="bg-gradient-to-br from-red-500 to-rose-500 shadow-red-500/30"
          darkMode={darkMode}
        >
          {unit.fuse_box_location && (
            <InfoItem icon={Zap} title={t('units.equipment.emergency.fuseBoxLocation')} darkMode={darkMode} variant="info">
              <p>{unit.fuse_box_location}</p>
            </InfoItem>
          )}

          {unit.water_shutoff_location && (
            <InfoItem icon={Droplets} title={t('units.equipment.emergency.waterShutoffLocation')} darkMode={darkMode} variant="info">
              <p>{unit.water_shutoff_location}</p>
            </InfoItem>
          )}

          {unit.first_aid_location && (
            <InfoItem icon={Heart} title={t('units.equipment.emergency.firstAidLocation')} darkMode={darkMode} variant="info">
              <p>{unit.first_aid_location}</p>
            </InfoItem>
          )}
        </EquipmentSection>
      )}

      {/* EV Charger */}
      {hasEV && (
        <EquipmentSection
          title={t('units.equipment.ev.title', 'Punionica za električni auto')}
          icon={Zap}
          iconColor="bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-500/30"
          darkMode={darkMode}
        >
          {(unit.ev_charger_type || unit.ev_charger_power_kw) && (
            <div className="flex flex-wrap gap-2">
              {unit.ev_charger_type && (
                <span className={cn('px-3 py-1 rounded-lg text-xs font-medium', darkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700')}>
                  {evChargerLabels[unit.ev_charger_type] || unit.ev_charger_type}
                </span>
              )}
              {unit.ev_charger_power_kw && (
                <span className={cn('px-3 py-1 rounded-lg text-xs font-medium', darkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700')}>
                  {unit.ev_charger_power_kw} kW
                </span>
              )}
            </div>
          )}
          {unit.ev_charger_instructions && (
            <InfoItem icon={Zap} title={t('units.equipment.ev.instructions', 'Upute za punionicu')} darkMode={darkMode}>
              <p className="whitespace-pre-wrap">{unit.ev_charger_instructions}</p>
            </InfoItem>
          )}
        </EquipmentSection>
      )}
    </div>
  );
}

export default GuestEquipmentSection;
